<template>
  <v-card>
    <v-toolbar color="titleBar" dark flat dense>
      <v-spacer />
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="loading || createDialog"
            v-on="on"
            @click="createDialog = true"
            icon
          >
            <v-icon>mdi-plus-circle</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("assign new IP or subnet") }}</span>
      </v-tooltip>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn :disabled="loading" v-on="on" @click="getItems" icon>
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("reload") }}</span>
      </v-tooltip>
    </v-toolbar>

    <v-card-text>
      <v-expand-transition>
        <ip-addresses-tenants-create
          v-if="createDialog"
          @canceled="createDialog = false"
          @created="
            createDialog = false;
            getItems();
          "
        />
      </v-expand-transition>

      <data-table-extended
        :headers="headers"
        :items="items"
        item-key="id"
        :loading="loading"
        :search="search"
        sort-by="link_identifier"
        :custom-filter="customFilter"
        :mobile-breakpoint="mbreakpoint"
        :hide-default-footer="items.length <= 15"
        :expanded.sync="expanded"
        single-expand
        show-expand
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [15, 50],
        }"
        open-expand-by-dbl-click-row
      >
        <template v-slot:top>
          <v-text-field
            class="mb-4"
            v-model="search"
            append-outer-icon="mdi-magnify"
            clear-icon="mdi-close-circle"
            :disabled="loading"
            :clearable="!isMobile"
            :label="$t('search')"
            single-line
            hide-details
          />
        </template>

        <template v-slot:item.type="{ item }">
          {{ $t(item.type) }}
        </template>

        <template v-slot:item.network="{ item }">
          {{ item.network_view }}
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <ip-addresses-tenants-details
              :item="item"
              class="my-6"
              @close="expanded = []"
              @deprovisioned="
                expanded = [];
                getItems();
              "
            />
          </td>
        </template>
      </data-table-extended>
    </v-card-text>
  </v-card>
</template>

<script>
import isMobile from "@/utils/mixins/isMobile";
import IpAddressesTenantsCreate from "@/components/services/ipaddr/IpAddressesTenantsCreate";
import IpAddressesTenantsDetails from "@/components/services/ipaddr/IpAddressesTenantsDetails";
import DataTableExtended from "@/components/basics/DataTableExtended";

const ip = require("@/utils/ip");

export default {
  name: "IpAddressesTenants",
  mixins: [isMobile],
  components: {
    IpAddressesTenantsCreate,
    IpAddressesTenantsDetails,
    DataTableExtended,
  },
  data: () => ({
    loading: false,
    createDialog: false,
    deleteLoading: false,
    deleteItem: null,
    items: [],
    search: "",
    expanded: [],
  }),
  computed: {
    headers() {
      return [
        { text: this.$i18n.t("link identifier"), value: "link_identifier" },
        { text: this.$i18n.t("type"), value: "type" },
        {
          text: this.$i18n.t("IP/subnet"),
          value: "network",
          sort: (a, b) => {
            return ip.compareCIDR(a, b);
          },
        },
        { text: this.$i18n.t("number of IPs"), value: "length" },
        { text: this.$i18n.t("IP version"), value: "version" },
        { text: "", value: "data-table-expand" },
      ];
    },
  },
  methods: {
    customFilter(value, search, item) {
      var addr = ip.parse(search);
      if (addr != null && item.cidr.contains(addr.toString())) return true;
      if (
        addr != null &&
        item.networks.filter(function (network) {
          return network.cidr.contains(addr.toString());
        }).length > 0
      )
        return true;
      return (
        (item.network && item.network.indexOf(search) >= 0) ||
        item.link_identifier.indexOf(search) >= 0
      );
    },
    getItems() {
      var that = this;
      that.loading = true;
      this.$http
        .get("services/ip-addresses/tenants")
        .then((response) => {
          that.items = response.data.map(function (item) {
            item.networks.forEach(function (network) {
              network.network = network.destination + "/" + network.prefix;
              network.cidr = ip.createCIDR(network.network);
              network.length = ip.length(network.network);
              network.link_identifier = item.link_identifier;
            });
            item.network_view =
              item.networks.length > 0
                ? item.networks
                    .map(function (item) {
                      return item.network;
                    })
                    .join(", ")
                : item.ip;

            item.type = item.networks.length > 0 ? "subnet" : "ip";
            item.length =
              item.networks.length > 0
                ? item.networks
                    .map(function (network) {
                      return network.length;
                    })
                    .reduce((a, b) => a + b, 0)
                : 1;
            item.network =
              item.networks.length > 0
                ? item.networks[0].network
                : item.ip + "/32";
            item.cidr = ip.createCIDR(
              item.ip + "/" + (item.version == 4 ? "32" : "128")
            );
            return item;
          });
        })
        .catch((err) => {
          that.$store.commit("setSystemError", {
            msg: err.message,
            title: err.title,
          });
        })
        .finally(function () {
          that.loading = false;
        });
    },
  },
  mounted: function () {
    this.getItems();
  },
};
</script>

<i18n>
{
  "en": {
    "reload":"reload",
    "link identifier": "Link identifier / OTO ID",
    "type": "Type",
    "IP/subnet": "IP/Subnet",
    "IP version": "IP version",
    "assign new IP or subnet": "assign new IP or subnet",
    "number of IPs": "Number of IPs",
    "delete": "delete",
    "delete IPs/subnets": "Delete IPs/subnets",
    "ip": "IP",
    "subnet": "Subnet"
  },
  "de": {
    "reload":"neu laden",
    "link identifier": "Link Identifikator / OTO ID",
    "type": "Typ",
    "IP/subnet": "IP/Subnetz",
    "IP version": "IP Version",
    "assign new IP or subnet": "neue IP oder Subnetz zuweisen",
    "number of IPs": "Anzahl IPs",
    "delete": "löschen",
    "delete IPs/subnets": "IPs/Subnetze löschen",
    "ip": "IP",
    "subnet": "Subnetz"
  }
}
</i18n>
